/**
 * Error handling utilities specifically designed to catch and handle
 * animation and DOM-related errors gracefully
 */

/**
 * Check if an error is animation-related
 * @param error The error object or message to check
 * @returns boolean indicating if the error is animation-related
 */
export const isAnimationError = (error: Error | string | unknown): boolean => {
  const errorMessage = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  return (
    errorMessage.includes("Cannot use 'in' operator to search for 'animation'") ||
    (errorMessage.includes("animation") && errorMessage.includes("undefined")) ||
    (errorMessage.includes("Cannot read property") && errorMessage.includes("animation")) ||
    (errorMessage.includes("Cannot set properties of undefined") && errorMessage.includes("animation"))
  );
};

/**
 * Check if an error is DOM position-related
 * @param error The error object or message to check
 * @returns boolean indicating if the error is position-related
 */
export const isPositionError = (error: Error | string | unknown): boolean => {
  const errorMessage = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  return (
    (errorMessage.includes("Cannot set properties of undefined") && errorMessage.includes("position")) ||
    (errorMessage.includes("Cannot read property") && errorMessage.includes("position")) ||
    (errorMessage.includes("Cannot read property") && 
      (errorMessage.includes("top") || 
       errorMessage.includes("left") || 
       errorMessage.includes("right") || 
       errorMessage.includes("bottom")))
  );
};

/**
 * Firestore localStorage-quota crash class (INCIDENT 2026-08-22).
 *
 * With multi-tab persistence on, the Firestore SDK coordinates tabs through
 * localStorage keys (`firestore_mutations_*`, `firestore_targets_*`, …). A
 * message flood filled the 5MB budget; `setItem` started throwing
 * QuotaExceededError inside the SDK, which then died with
 * "FIRESTORE (x.y.z) INTERNAL ASSERTION FAILED: Unexpected state (ID: b815)"
 * — a red error wall the user cannot recover from without knowing to clear
 * site data by hand. Users must never need to know that (2026-08-20 cache
 * doctrine, same spirit): detect the class, clear the SDK's own keys, reload
 * once. Precision-first: a quota error is only claimed when the message also
 * names firestore — some other feature filling storage is not this class.
 */
export const isFirestoreQuotaCrash = (error: Error | string | unknown): boolean => {
  let name = '';
  let text = '';
  if (typeof error === 'string') {
    text = error;
  } else if (error && typeof error === 'object') {
    // Not `instanceof Error`: DOMException (QuotaExceededError's real type)
    // fails that check in some browsers, and event.reason can be anything.
    name = String((error as { name?: unknown }).name ?? '');
    const message = String((error as { message?: unknown }).message ?? '');
    text = name ? `${name}: ${message || String(error)}` : message || String(error);
  } else {
    text = String(error ?? '');
  }
  if (/FIRESTORE.+INTERNAL ASSERTION FAILED/i.test(text)) return true;
  const isQuota = name === 'QuotaExceededError' || /QuotaExceededError|exceeded the quota/i.test(text);
  return isQuota && /firestore/i.test(text);
};

const QUOTA_SELF_HEAL_STAMP_KEY = 'prest0n_firestore_quota_selfheal_at';
const QUOTA_SELF_HEAL_MIN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Clear the Firestore SDK's localStorage keys and reload — once. The
 * sessionStorage stamp (separate quota budget from localStorage, so it has
 * room even when localStorage is full) guarantees at most one reload per
 * window: if the heal did not stick, the error surfaces normally instead of
 * reload-looping. Returns true iff a reload was initiated.
 *
 * Dropping `firestore_*` keys is safe-by-comparison, not free: any client
 * mutations still queued in a crashed tab are abandoned. That tab was already
 * dead — the SDK had halted on an internal assertion — and a clean reload
 * resumes from the server + IndexedDB, which is strictly better than a
 * permanently red page.
 */
export const attemptFirestoreQuotaSelfHeal = (): boolean => {
  try {
    try {
      const last = Number(window.sessionStorage.getItem(QUOTA_SELF_HEAL_STAMP_KEY) || 0);
      if (Number.isFinite(last) && last > 0 && Date.now() - last < QUOTA_SELF_HEAL_MIN_INTERVAL_MS) {
        return false; // healed recently and still crashing — do not loop
      }
      window.sessionStorage.setItem(QUOTA_SELF_HEAL_STAMP_KEY, String(Date.now()));
    } catch {
      return false; // cannot guard against a loop → do not reload at all
    }
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firestore_')) doomed.push(key);
    }
    for (const key of doomed) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // a single stuck key must not stop the rest
      }
    }
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

/**
 * Setup global error handlers for animation and DOM-related errors
 */
export const setupGlobalErrorHandlers = (): void => {
  // Store original console methods
  const originalConsoleError = console.error;
  
  // Patch console.error to intercept animation errors
  console.error = function(...args: any[]) {
    // Firestore quota crash: the SDK logs the internal assertion through its
    // own logger (console.error) besides throwing, and the throw sometimes
    // dies inside SDK internals no listener sees — so this patch is a catch
    // point too. Log FIRST (the evidence must survive for support), then heal.
    if (args.some((arg) => isFirestoreQuotaCrash(arg))) {
      originalConsoleError.apply(console, args);
      attemptFirestoreQuotaSelfHeal();
      return;
    }

    // Check if this is an animation-related error we want to handle gracefully
    const isAnimation = args.some(arg =>
      typeof arg === 'string' && isAnimationError(arg)
    );

    const isPosition = args.some(arg =>
      typeof arg === 'string' && isPositionError(arg)
    );

    // If it's a handled error type, downgrade to warning
    if (isAnimation) {
      console.warn('Animation error intercepted:', ...args);
      return;
    }
    
    if (isPosition) {
      console.warn('Position error intercepted:', ...args);
      return;
    }
    
    // Otherwise, use the original error handler
    originalConsoleError.apply(console, args);
  };
  
  // Handle uncaught promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isFirestoreQuotaCrash(event.reason)) {
      // Only swallow the rejection when a reload is actually underway;
      // if the heal declined (recent reload, guard unusable) the error
      // must stay visible.
      if (attemptFirestoreQuotaSelfHeal()) event.preventDefault();
      return;
    }
    if (isAnimationError(event.reason) || isPositionError(event.reason)) {
      // Prevent the error from bubbling up and crashing the app
      event.preventDefault();
      console.warn('Suppressed error in promise:', event.reason);
    }
  });

  // Handle uncaught exceptions
  window.addEventListener('error', (event) => {
    if (isFirestoreQuotaCrash(event.error) || isFirestoreQuotaCrash(event.message)) {
      if (attemptFirestoreQuotaSelfHeal()) {
        event.preventDefault();
        return true;
      }
      return false;
    }
    if (isAnimationError(event.error) || isPositionError(event.error)) {
      // Prevent the error from bubbling up and crashing the app
      event.preventDefault();
      console.warn('Suppressed error:', event.error);
      return true; // Prevents the firing of the default event handler
    }
    return false; // Let other errors propagate normally
  });
};

/**
 * Create a safe version of a function that might throw animation or position errors
 * @param fn The function to make safe
 * @returns A wrapped version of the function that won't throw
 */
export function makeSafe<T extends (...args: any[]) => any>(
  fn: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return (...args: Parameters<T>): ReturnType<T> | undefined => {
    try {
      return fn(...args);
    } catch (error) {
      if (isAnimationError(error) || isPositionError(error)) {
        console.warn('Caught and suppressed error in function:', error);
        return undefined;
      }
      throw error; // Re-throw any other errors
    }
  };
} 